import { useDependencies } from '@servicetitan/react-ioc';
import { CustomersApi } from 'api';
import axios from 'axios';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Fragment, useState } from 'react';
import { FeatureFlagKeys } from '../../../../feature-flags';
import { BookButton } from './book-button';
import { BookingContextPair, flattenBookingContextPairs } from './booking-context';
import { BookingContextPicker } from './booking-context-picker';
import { useSchedulingPro } from './scheduling-pro-provider';

const BOOKING_CONTEXT_STORAGE_KEY = 'se-customer-context';
const RESOLVE_TIMEOUT_MS = 3000;

const openScheduler = () => {
    window._scheduler?.show();
};

/** sessionStorage can throw (quota, private-browsing) -- never let that dead-end Book Us. */
const writeBookingContext = (pair: BookingContextPair) => {
    try {
        sessionStorage.setItem(
            BOOKING_CONTEXT_STORAGE_KEY,
            JSON.stringify({ customerId: pair.customerId, locationId: pair.locationId })
        );
    } catch {
        // sessionStorage unavailable -- show() still opens, unprefilled or with a stale value.
    }
};

/**
 * Clears a stale pair from an earlier confirmed pick so it can't silently
 * pre-fill the wrong customer on a later click that doesn't resolve a fresh
 * one -- including a click after the flag has been turned back off.
 */
const clearBookingContext = () => {
    try {
        sessionStorage.removeItem(BOOKING_CONTEXT_STORAGE_KEY);
    } catch {
        // sessionStorage unavailable -- nothing to clear.
    }
};

interface SchedulingProWidgetProps {
    compact?: boolean;
}

export const SchedulingProWidget = ({ compact }: SchedulingProWidgetProps) => {
    const { schedulingProScriptLoadedSuccessfully } = useSchedulingPro();
    const [customersApi] = useDependencies(CustomersApi);
    const { enableBookUsCustomerContext } = useFlags<FeatureFlagKeys>();
    const [resolving, setResolving] = useState(false);
    const [pickerPairs, setPickerPairs] = useState<BookingContextPair[] | undefined>(undefined);

    const resolveBookingContext = async () => {
        const cancelSource = axios.CancelToken.source();
        const timeoutId = setTimeout(() => cancelSource.cancel(), RESOLVE_TIMEOUT_MS);

        try {
            const customers = await customersApi.getServiceAddresses(cancelSource.token);
            const pairs = flattenBookingContextPairs(customers);

            if (pairs.length === 0) {
                clearBookingContext();
                openScheduler();
            } else if (pairs.length === 1) {
                writeBookingContext(pairs[0]);
                openScheduler();
            } else {
                setPickerPairs(pairs);
            }
        } catch {
            // Failure or timeout -- degrade to today's manual-entry behavior.
            clearBookingContext();
            openScheduler();
        } finally {
            clearTimeout(timeoutId);
            setResolving(false);
        }
    };

    const handleClick = () => {
        if (resolving) {
            return;
        }
        if (!enableBookUsCustomerContext) {
            clearBookingContext();
            openScheduler();
            return;
        }
        setResolving(true);
        resolveBookingContext();
    };

    const handlePickerConfirm = (pair: BookingContextPair) => {
        setPickerPairs(undefined);
        writeBookingContext(pair);
        openScheduler();
    };

    const handlePickerDismiss = () => {
        setPickerPairs(undefined);
    };

    if (!schedulingProScriptLoadedSuccessfully) {
        return null;
    }

    return (
        <Fragment>
            <BookButton
                onClick={handleClick}
                disabled={resolving}
                loading={resolving}
                compact={compact}
            />
            {pickerPairs && (
                <BookingContextPicker
                    pairs={pickerPairs}
                    onConfirm={handlePickerConfirm}
                    onDismiss={handlePickerDismiss}
                />
            )}
        </Fragment>
    );
};
